import React, { useEffect, useRef, useState } from 'react';
import { XCircle } from 'lucide-react';

const TheProblem = () => {
  const [visibleItems, setVisibleItems] = useState([]);
  const sectionRef = useRef(null);
  const itemRefs = useRef([]);

  useEffect(() => {
    const headerObs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisibleItems(p => [...new Set([...p, 'header'])]); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) headerObs.observe(sectionRef.current);

    const itemObs = itemRefs.current.map((ref, i) => {
      const obs = new IntersectionObserver(
        ([e]) => {
          if (e.isIntersecting) {
            setTimeout(() => setVisibleItems(p => [...new Set([...p, i])]), i * 110);
          }
        },
        { threshold: 0.1 }
      );
      if (ref) obs.observe(ref);
      return obs;
    });

    return () => { headerObs.disconnect(); itemObs.forEach(o => o.disconnect()); };
  }, []);

  const problems = [
    "You're stuck with inconsistent brand deals as your only income",
    "Your audience trusts you, but you don't have a product to sell them",
    "You know you should create digital products, but you're overwhelmed",
    "You're trading time for money and burning out",
    "Every platform change could wipe out your income overnight"
  ];

  return (
    <section ref={sectionRef} className="py-24 px-6 bg-[rgb(26,28,30)] relative overflow-hidden">
      <div className="absolute top-[-10%] left-[-5%] w-[500px] h-[500px] bg-[rgba(124,56,178,0.05)] blur-[120px] rounded-full pointer-events-none"></div>

      <div className="max-w-[1000px] mx-auto relative z-10">
        <div className={`text-center mb-16 transition-all duration-700 ${
          visibleItems.includes('header') ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-10 scale-95'
        }`}>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            The <span className="bg-gradient-to-r from-[rgb(197,155,47)] to-[rgb(224,194,95)] text-transparent bg-clip-text">Problem</span>
          </h2>
          <p className="text-xl text-[rgb(218,218,218)] max-w-[700px] mx-auto">
            Most creators are one algorithm change away from losing everything
          </p>
        </div>

        <div className="space-y-4">
          {problems.map((problem, index) => (
            <div
              key={index}
              ref={el => (itemRefs.current[index] = el)}
              className={`flex items-start space-x-4 bg-[rgb(38,40,42)] border border-[rgba(255,255,255,0.1)] rounded-xl p-6 transition-all duration-500 hover:border-[rgb(197,155,47)] hover:shadow-[0_4px_20px_rgba(197,155,47,0.1)] hover:-translate-y-1 ${
                visibleItems.includes(index) ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'
              }`}
            >
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-500/20 border border-red-500/40 flex items-center justify-center">
                <XCircle size={18} className="text-red-400" />
              </div>
              <p className="text-[rgb(218,218,218)] text-lg leading-relaxed">
                {problem}
              </p>
            </div>
          ))}
        </div>

        <div className={`mt-12 text-center transition-all duration-700 ${
          visibleItems.includes(problems.length - 1) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`} style={{ transitionDelay: '150ms' }}>
          <p className="text-2xl font-semibold text-[rgb(197,155,47)]">
            Sound familiar?
          </p>
        </div>
      </div>
    </section>
  );
};

export default TheProblem;
